// // ============================================================
// // src/components/Pricing.js
// // ✅ Passes hospitalId to fetch only this hospital's pricing
// // ============================================================
// import React, { useState, useEffect, useCallback } from "react";
// import { useHospital } from "../context/HospitalContext";

// const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:5000/api";

// const DEFAULT_PRICING = [
//   {
//     icon: "🦷",
//     name: "Essential Care",
//     price: "Rs. 500",
//     from: true,
//     featured: false,
//     features: [
//       "Oral Examination",
//       "Professional Cleaning",
//       "X-Ray (2 films)",
//       "Cavity Check",
//       "Treatment Plan",
//       "Follow-up Reminder",
//     ],
//   },
//   {
//     icon: "⭐",
//     name: "Smile Makeover",
//     price: "Rs. 3,500",
//     from: true,
//     featured: true,
//     features: [
//       "Teeth Whitening (1 hr)",
//       "Composite Bonding",
//       "Gum Contouring",
//       "Digital Smile Design",
//       "Post-care Kit",
//       "3-Month Follow-up",
//     ],
//   },
//   {
//     icon: "💎",
//     name: "Implant Package",
//     price: "Rs. 25,000",
//     from: true,
//     featured: false,
//     features: [
//       "Titanium Implant",
//       "3D Cone Beam CT Scan",
//       "Custom Crown",
//       "Bone Grafting (if needed)",
//       "Lifetime Warranty",
//       "Priority Scheduling",
//     ],
//   },
// ];

// export default function Pricing() {
//   const { hospital } = useHospital();
//   const [pricing, setPricing] = useState(DEFAULT_PRICING);

//   const fetchPricing = useCallback(() => {
//     // ✅ Pass hospitalId as query param — gets THIS hospital's pricing only
//     const hospitalId = hospital?._id || "";
//     const url = hospitalId
//       ? `${BASE_URL}/pricing?hospitalId=${hospitalId}`
//       : `${BASE_URL}/pricing`;

//     fetch(url)
//       .then((r) => (r.ok ? r.json() : null))
//       .then((data) => {
//         if (data && data.length > 0) setPricing(data);
//       })
//       .catch(() => {});
//   }, [hospital?._id]);

//   useEffect(() => {
//     fetchPricing();
//     window.addEventListener("focus", fetchPricing);
//     return () => window.removeEventListener("focus", fetchPricing);
//   }, [fetchPricing]);

//   useEffect(() => {
//     window.__refreshPricing = fetchPricing;
//     return () => {
//       delete window.__refreshPricing;
//     };
//   }, [fetchPricing]);

//   const scrollTo = (id) =>
//     document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

//   return (
//     <section className="pricing" id="pricing">
//       <div className="pricing-bg" />
//       <div className="pricing-header">
//         <div className="section-tag">Transparent Pricing</div>
//         <h2 className="section-title" style={{ color: "var(--white)" }}>
//           Clear &amp; <em style={{ color: "var(--gold)" }}>Honest Pricing</em>
//         </h2>
//         <p className="section-desc">
//           No hidden fees. No surprises. Just honest, transparent pricing for
//           world-class dental care.
//         </p>
//       </div>
//       <div className="pricing-grid">
//         {pricing.map((p, i) => (
//           <div
//             className={`pricing-card${p.featured ? " featured" : ""}`}
//             key={i}
//           >
//             <div className="pc-icon">{p.icon}</div>
//             <div className="pc-name">{p.name}</div>
//             <div className="pc-price">{p.price}</div>
//             <div className="pc-from">
//               {p.from ? "Starting price" : "Fixed price"}
//             </div>
//             <div className="pc-divider" />
//             <ul className="pc-features">
//               {(p.features || []).map((f, j) => (
//                 <li key={j}>{f}</li>
//               ))}
//             </ul>
//             <button className="pc-btn" onClick={() => scrollTo("appointment")}>
//               Book This Package
//             </button>
//           </div>
//         ))}
//       </div>
//       <p className="pricing-note">
//         * Prices are indicative. Final cost depends on clinical assessment. EMI
//         &amp; insurance accepted.
//       </p>
//     </section>
//   );
// }
// ============================================================
// src/components/Pricing.js
// ✅ Specialty-specific default pricing per hospital type
// ============================================================
import React, { useState, useEffect, useCallback } from "react";
import { useHospital } from "../context/HospitalContext";
import { getSpecialtyText } from "../utils/specialtyText";

const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:5000/api";

// ✅ Default pricing per specialty
const DEFAULT_PRICING_BY_TYPE = {
  dental: [
    {
      icon: "🦷",
      name: "Essential Care",
      price: "Rs. 500",
      from: true,
      featured: false,
      features: [
        "Oral Examination",
        "Professional Cleaning",
        "X-Ray (2 films)",
        "Cavity Check",
        "Treatment Plan",
        "Follow-up Reminder",
      ],
    },
    {
      icon: "⭐",
      name: "Smile Makeover",
      price: "Rs. 3,500",
      from: true,
      featured: true,
      features: [
        "Teeth Whitening (1 hr)",
        "Composite Bonding",
        "Gum Contouring",
        "Digital Smile Design",
        "Post-care Kit",
        "3-Month Follow-up",
      ],
    },
    {
      icon: "💎",
      name: "Implant Package",
      price: "Rs. 25,000",
      from: true,
      featured: false,
      features: [
        "Titanium Implant",
        "3D Cone Beam CT Scan",
        "Custom Crown",
        "Bone Grafting (if needed)",
        "Lifetime Warranty",
        "Priority Scheduling",
      ],
    },
  ],
  ayurved: [
    {
      icon: "🌿",
      name: "Wellness Starter",
      price: "Rs. 1,500",
      from: true,
      featured: false,
      features: [
        "Initial Prakriti Analysis",
        "Dosha Assessment",
        "Herbal Consultation",
        "Diet & Lifestyle Plan",
        "Follow-up Call",
        "Home Remedy Guide",
      ],
    },
    {
      icon: "⭐",
      name: "Panchakarma Detox",
      price: "Rs. 8,000",
      from: true,
      featured: true,
      features: [
        "Abhyanga (Full Body Massage)",
        "Shirodhara Therapy",
        "Nasya Treatment",
        "Herbal Steam Bath",
        "Medicated Diet Plan",
        "7-Day Program",
      ],
    },
    {
      icon: "💎",
      name: "Complete Wellness",
      price: "Rs. 18,000",
      from: true,
      featured: false,
      features: [
        "Full Panchakarma Program",
        "Rasayana Therapy",
        "Customised Herbal Medicines",
        "Yoga & Pranayama",
        "21-Day Program",
        "Priority Support",
      ],
    },
  ],
  ortho: [
    {
      icon: "🦴",
      name: "OPD Consultation",
      price: "Rs. 800",
      from: true,
      featured: false,
      features: [
        "Orthopedic Assessment",
        "X-Ray Review",
        "Pain Diagnosis",
        "Treatment Planning",
        "Physiotherapy Advice",
        "Follow-up Reminder",
      ],
    },
    {
      icon: "⭐",
      name: "Joint Care Package",
      price: "Rs. 15,000",
      from: true,
      featured: true,
      features: [
        "MRI / CT Scan Review",
        "Specialist Consultation",
        "Injection Therapy",
        "Physiotherapy (10 sessions)",
        "Custom Brace",
        "Monthly Follow-up",
      ],
    },
    {
      icon: "💎",
      name: "Surgical Package",
      price: "Rs. 1,20,000",
      from: true,
      featured: false,
      features: [
        "Joint Replacement Surgery",
        "Pre-op Assessment",
        "Post-op Physiotherapy",
        "6-Month Rehabilitation",
        "Implant Warranty",
        "24/7 Support",
      ],
    },
  ],
  eye: [
    {
      icon: "👁️",
      name: "Eye Checkup",
      price: "Rs. 500",
      from: true,
      featured: false,
      features: [
        "Comprehensive Eye Test",
        "Visual Acuity Check",
        "Intraocular Pressure",
        "Fundus Examination",
        "Spectacle Prescription",
        "Follow-up Reminder",
      ],
    },
    {
      icon: "⭐",
      name: "LASIK Package",
      price: "Rs. 35,000",
      from: true,
      featured: true,
      features: [
        "Pre-LASIK Screening",
        "Bladeless LASIK Surgery",
        "Both Eyes Covered",
        "Post-op Medications",
        "3-Month Follow-up",
        "Lifetime Enhancement",
      ],
    },
    {
      icon: "💎",
      name: "Cataract Surgery",
      price: "Rs. 45,000",
      from: true,
      featured: false,
      features: [
        "Phacoemulsification",
        "Premium IOL Implant",
        "Both Eyes",
        "Pre & Post-op Care",
        "Priority Scheduling",
        "1-Year Follow-up",
      ],
    },
  ],
  cardiology: [
    {
      icon: "❤️",
      name: "Cardiac Checkup",
      price: "Rs. 2,500",
      from: true,
      featured: false,
      features: [
        "ECG",
        "Echocardiography",
        "Blood Pressure Check",
        "Cholesterol Test",
        "Cardiologist Consultation",
        "Diet & Lifestyle Plan",
      ],
    },
    {
      icon: "⭐",
      name: "Heart Care Package",
      price: "Rs. 12,000",
      from: true,
      featured: true,
      features: [
        "Stress Test (TMT)",
        "2D Echo",
        "Holter Monitoring",
        "Lipid Profile",
        "Specialist Review",
        "3-Month Follow-up",
      ],
    },
    {
      icon: "💎",
      name: "Angioplasty Package",
      price: "Rs. 2,50,000",
      from: true,
      featured: false,
      features: [
        "Coronary Angiography",
        "Angioplasty & Stenting",
        "ICU Care",
        "Post-op Medications",
        "Cardiac Rehab",
        "6-Month Follow-up",
      ],
    },
  ],
  pediatric: [
    {
      icon: "👶",
      name: "Well Baby Checkup",
      price: "Rs. 500",
      from: true,
      featured: false,
      features: [
        "Growth Monitoring",
        "Developmental Assessment",
        "Vaccination Review",
        "Nutrition Counseling",
        "Parent Guidance",
        "Follow-up Reminder",
      ],
    },
    {
      icon: "⭐",
      name: "Child Health Package",
      price: "Rs. 3,000",
      from: true,
      featured: true,
      features: [
        "Complete Blood Count",
        "Urine Analysis",
        "Pediatric Consultation",
        "Immunization Update",
        "Diet Plan",
        "Monthly Follow-up",
      ],
    },
    {
      icon: "💎",
      name: "Annual Health Plan",
      price: "Rs. 8,000",
      from: true,
      featured: false,
      features: [
        "4 Quarterly Checkups",
        "All Vaccinations",
        "Growth Chart Tracking",
        "School Health Certificate",
        "Priority Access",
        "24/7 Helpline",
      ],
    },
  ],
  general: [
    {
      icon: "🏥",
      name: "General OPD",
      price: "Rs. 500",
      from: true,
      featured: false,
      features: [
        "Doctor Consultation",
        "Basic Examination",
        "Prescription",
        "Health Advice",
        "Follow-up Reminder",
        "Lab Referral if needed",
      ],
    },
    {
      icon: "⭐",
      name: "Health Checkup Package",
      price: "Rs. 4,500",
      from: true,
      featured: true,
      features: [
        "Complete Blood Count",
        "Blood Sugar & Lipids",
        "Liver & Kidney Function",
        "ECG",
        "Chest X-Ray",
        "Doctor Review & Report",
      ],
    },
    {
      icon: "💎",
      name: "Comprehensive Plan",
      price: "Rs. 12,000",
      from: true,
      featured: false,
      features: [
        "Full Body Checkup",
        "Specialist Consultation",
        "Ultrasound Abdomen",
        "Thyroid Profile",
        "Annual Membership",
        "Priority Scheduling",
      ],
    },
  ],
};

export default function Pricing() {
  const { hospital } = useHospital();
  const t = getSpecialtyText(hospital.type);

  // ✅ Use specialty-specific defaults
  const type = (hospital.type || "dental").toLowerCase();
  const defaultPricing =
    DEFAULT_PRICING_BY_TYPE[type] || DEFAULT_PRICING_BY_TYPE.dental;

  const [pricing, setPricing] = useState(defaultPricing);

  const fetchPricing = useCallback(() => {
    const hospitalId = hospital?._id || "";
    const url = hospitalId
      ? `${BASE_URL}/pricing?hospitalId=${hospitalId}`
      : `${BASE_URL}/pricing`;
    fetch(url)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (data && data.length > 0) setPricing(data);
        else setPricing(defaultPricing); // ✅ Fall back to specialty default
      })
      .catch(() => setPricing(defaultPricing));
  }, [hospital?._id, hospital?.type]); // eslint-disable-line

  useEffect(() => {
    fetchPricing();
    window.addEventListener("focus", fetchPricing);
    return () => window.removeEventListener("focus", fetchPricing);
  }, [fetchPricing]);

  useEffect(() => {
    window.__refreshPricing = fetchPricing;
    return () => {
      delete window.__refreshPricing;
    };
  }, [fetchPricing]);

  const scrollTo = (id) =>
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section className="pricing" id="pricing">
      <div className="pricing-bg" />
      <div className="pricing-header">
        <div className="section-tag">Transparent Pricing</div>
        <h2 className="section-title" style={{ color: "var(--white)" }}>
          Clear &amp; <em style={{ color: "var(--gold)" }}>Honest Pricing</em>
        </h2>
        <p className="section-desc">
          No hidden fees. No surprises. Just honest, transparent pricing for
          world-class {t.navTagline.toLowerCase()}.
        </p>
      </div>
      <div className="pricing-grid">
        {pricing.map((p, i) => (
          <div
            className={`pricing-card${p.featured ? " featured" : ""}`}
            key={i}
          >
            <div className="pc-icon">{p.icon}</div>
            <div className="pc-name">{p.name}</div>
            <div className="pc-price">{p.price}</div>
            <div className="pc-from">
              {p.from ? "Starting price" : "Fixed price"}
            </div>
            <div className="pc-divider" />
            <ul className="pc-features">
              {(p.features || []).map((f, j) => (
                <li key={j}>{f}</li>
              ))}
            </ul>
            <button className="pc-btn" onClick={() => scrollTo("appointment")}>
              Book This Package
            </button>
          </div>
        ))}
      </div>
      <p className="pricing-note">
        * Prices are indicative. Final cost depends on clinical assessment. EMI
        &amp; insurance accepted.
      </p>
    </section>
  );
}
